(() => {
  const MAX_SLIDES = 3;
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const shuffle = (items) => {
    const shuffled = [...items];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }

    return shuffled;
  };

  const hydrateSlide = (slide, slideIndex) => {
    slide.querySelectorAll('[data-slider-src]').forEach((image) => {
      image.src = image.dataset.sliderSrc;
      if (image.dataset.sliderSrcset) image.srcset = image.dataset.sliderSrcset;
      if (image.dataset.sliderSizes) image.sizes = image.dataset.sliderSizes;
      image.loading = slideIndex === 0 ? 'eager' : 'lazy';
      image.decoding = 'async';

      if (slideIndex === 0) image.fetchPriority = 'high';

      delete image.dataset.sliderSrc;
      delete image.dataset.sliderSrcset;
      delete image.dataset.sliderSizes;
    });
  };

  const initializeSlider = (slider) => {
    if (slider.dataset.sliderInitialized === 'true') return;
    slider.dataset.sliderInitialized = 'true';

    const track = slider.querySelector('[data-product-slider-track]');
    const controls = slider.querySelector('[data-product-slider-controls]');
    const status = slider.querySelector('[data-product-slider-status]');
    const candidates = [...slider.querySelectorAll('[data-product-slide-candidate]')];
    const primary = slider.querySelector('[data-product-slide-primary]') || candidates[0];

    if (!track || !controls || !primary || candidates.length === 0) {
      slider.hidden = true;
      return;
    }

    const randomCandidates = shuffle(candidates.filter((candidate) => candidate !== primary));
    const slides = [primary, ...randomCandidates.slice(0, MAX_SLIDES - 1)];
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    let activeIndex = 0;
    let scrollFrame = null;
    let userScrolling = false;
    let scrollEndTimer = null;

    track.replaceChildren(...slides);

    slides.forEach((slide, index) => {
      slide.hidden = false;
      slide.id = `${slider.closest('[data-collection-section]')?.dataset.collectionSection || 'collection'}-ProductSlide-${index + 1}`;
      slide.setAttribute(
        'aria-label',
        `Slide ${index + 1} of ${slides.length}: ${slide.dataset.slideTitle || 'Product image'}. View product`,
      );
      hydrateSlide(slide, index);
    });

    const controlButtons = slides.map((slide, index) => {
      const button = document.createElement('button');
      const label = document.createElement('span');

      button.className = 'aarke-product-slider__control';
      button.type = 'button';
      button.setAttribute('aria-controls', slide.id);
      button.setAttribute('aria-label', `Show slide ${index + 1} of ${slides.length}`);
      label.className = 'visually-hidden';
      label.textContent = `${index + 1} of ${slides.length}`;
      button.append(label);
      controls.append(button);

      return button;
    });

    const announce = (index) => {
      if (!status) return;
      status.setAttribute('aria-live', 'polite');
      status.textContent = `Slide ${index + 1} of ${slides.length}: ${slides[index].dataset.slideTitle || 'Product image'}`;
    };

    const updateActiveSlide = (index, { shouldAnnounce = false } = {}) => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      activeIndex = nextIndex;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.tabIndex = isActive ? 0 : -1;
      });

      controlButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === nextIndex;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      if (shouldAnnounce) announce(nextIndex);
    };

    const goToSlide = (index, { shouldAnnounce = true } = {}) => {
      updateActiveSlide(index, { shouldAnnounce });
      track.scrollTo({
        left: index * track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    controlButtons.forEach((button, index) => {
      button.addEventListener('click', () => goToSlide(index));
    });

    track.addEventListener('pointerdown', () => {
      userScrolling = true;
    });

    track.addEventListener('wheel', () => {
      userScrolling = true;
    }, { passive: true });

    track.addEventListener('scroll', () => {
      if (scrollFrame !== null) return;

      scrollFrame = window.requestAnimationFrame(() => {
        const nextIndex = track.clientWidth > 0
          ? Math.round(track.scrollLeft / track.clientWidth)
          : 0;
        if (nextIndex !== activeIndex) {
          updateActiveSlide(nextIndex, { shouldAnnounce: userScrolling });
        }
        scrollFrame = null;
      });

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(() => {
        userScrolling = false;
      }, 180);
    }, { passive: true });

    track.addEventListener('keydown', (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowLeft') nextIndex = activeIndex - 1;
      if (event.key === 'ArrowRight') nextIndex = activeIndex + 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = slides.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      goToSlide(Math.max(0, Math.min(nextIndex, slides.length - 1)));
    });

    window.addEventListener('resize', () => {
      track.scrollTo({ left: activeIndex * track.clientWidth, behavior: 'auto' });
    });

    if (slides.length > 1) {
      controls.hidden = false;
    } else {
      track.tabIndex = -1;
      slider.classList.add('is-static');
    }

    slider.classList.add('is-enhanced');
    updateActiveSlide(0);
  };

  const initializeWithin = (root = document) => {
    if (root.matches?.('[data-product-slider]')) initializeSlider(root);
    root.querySelectorAll?.('[data-product-slider]').forEach(initializeSlider);
  };

  initializeWithin();
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
